import React from 'react';
import { ExecutionLedger, LedgerStep, LedgerStepStatus } from '../../../types';

// ─── Step Status Icons ───
const StatusIcon: React.FC<{ status: LedgerStepStatus }> = ({ status }) => {
  switch (status) {
    case 'COMPLETED':
      return (
        <div className="w-6 h-6 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center shrink-0">
          <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
      );
    case 'IN_PROGRESS':
      return (
        <div className="w-6 h-6 rounded-full bg-white dark:bg-[#171717] border border-gray-300 dark:border-gray-600 flex items-center justify-center shrink-0">
          <div className="w-3 h-3 rounded-full border-2 border-black dark:border-white border-t-transparent animate-spin" />
        </div>
      );
    case 'FAILED':
      return (
        <div className="w-6 h-6 rounded-full bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 flex items-center justify-center shrink-0">
          <svg className="w-3.5 h-3.5 text-red-600 dark:text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </div>
      );
    case 'WAITING_USER':
      return (
        <div className="w-6 h-6 rounded-full bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 flex items-center justify-center shrink-0">
          <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
        </div>
      );
    case 'PENDING':
    default:
      return (
        <div className="w-6 h-6 rounded-full bg-gray-50 dark:bg-[#111] border border-gray-200 dark:border-gray-800 flex items-center justify-center shrink-0">
          <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
      );
  }
};

// ─── Single Step Row ───
const LedgerStepRow: React.FC<{ step: LedgerStep; isLast: boolean }> = ({ step, isLast }) => {
  const isActive = step.status === 'IN_PROGRESS';
  const isDone = step.status === 'COMPLETED';
  const isFailed = step.status === 'FAILED';

  return (
    <div className="flex gap-3 relative">
      {/* Vertical Connector Line */}
      {!isLast && (
        <div className="absolute left-3 top-6 w-px bottom-0 bg-gray-200 dark:bg-gray-800" />
      )}

      {/* Status Icon */}
      <StatusIcon status={step.status} />

      {/* Step Content */}
      <div className={`flex-1 min-w-0 pb-4 ${isLast ? 'pb-0' : ''}`}>
        <p className={`text-[13px] leading-snug ${
          isActive ? 'text-black dark:text-white font-semibold' :
          isDone ? 'text-gray-600 dark:text-gray-400' :
          isFailed ? 'text-red-600 dark:text-red-400' :
          'text-gray-400 dark:text-gray-500'
        }`}>
          {step.description}
        </p>

        {/* Output Summary (shown when completed or failed) */}
        {step.output_summary && (isDone || isFailed) && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 truncate">
            {step.output_summary}
          </p>
        )}
      </div>
    </div>
  );
};

// ─── Main Ledger Component ───
const NeuraLedgerStatus: React.FC<{ ledger: ExecutionLedger }> = ({ ledger }) => {
  const completedCount = ledger.steps.filter(s => s.status === 'COMPLETED').length;
  const totalCount = ledger.steps.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const isAllDone = completedCount === totalCount;
  const hasFailed = ledger.steps.some(s => s.status === 'FAILED');

  return (
    <div className="w-full rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#0a0a0a] overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${
            isAllDone ? 'bg-emerald-500' : hasFailed ? 'bg-red-500' : 'bg-black dark:bg-white animate-pulse'
          }`} />
          <p className="text-[13px] font-semibold text-black dark:text-white truncate">
            {ledger.task_summary}
          </p>
        </div>
        <span className="text-[11px] font-mono text-gray-400 dark:text-gray-500 shrink-0 ml-2">
          {completedCount}/{totalCount}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-0.5 bg-gray-100 dark:bg-gray-900">
        <div
          className={`h-full transition-all duration-700 ease-out ${
            hasFailed ? 'bg-red-500' : isAllDone ? 'bg-emerald-500' : 'bg-black dark:bg-white'
          }`}
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Steps List */}
      <div className="px-4 py-3">
        {ledger.steps.map((step, idx) => (
          <LedgerStepRow
            key={step.id}
            step={step}
            isLast={idx === ledger.steps.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

export default NeuraLedgerStatus;
